import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Spinner, Prohibit } from '@phosphor-icons/react';
import { FormRenderer } from '@/components/FormRenderer';
import { ERS_LABEL } from '@/config/legal';

const API = process.env.REACT_APP_BACKEND_URL;
const imageUrl = (path) => `${API}/api/public/files/${path}`;

export default function PublicFormPage() {
  const { slug } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | unavailable | done
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/forms/${slug}`).then(({ data }) => {
      if (data.available) { setForm(data.form); setState('ready'); }
      else { setMessage(data.message || ''); setState('unavailable'); }
    }).catch(() => { setMessage('Não foi possível carregar este formulário.'); setState('unavailable'); });
  }, [slug]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${API}/api/public/forms/${slug}/upload`, fd);
    return data.path;
  };

  const handleSubmit = async (answers, consentAccepted, website) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/forms/${slug}/submit`, {
        answers, consent_accepted: consentAccepted, consent_version: form.consent_version || '', website,
      });
      setMessage(data.message || 'Obrigada!');
      setState('done');
    } catch (e) {
      setMessage(e?.response?.data?.detail || 'Não foi possível submeter. Tenta novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center px-4 py-8" data-testid="public-form-page">
      <div className="w-full max-w-md">
        {state === 'loading' && (
          <div className="flex justify-center py-24"><Spinner size={28} className="animate-spin text-brand-green" /></div>
        )}

        {state === 'unavailable' && (
          <div className="bg-white rounded-3xl border border-brand-border p-8 text-center mt-16" data-testid="form-unavailable">
            <Prohibit size={32} className="text-brand-muted mx-auto mb-3" />
            <p className="text-brand-text font-medium">{message || 'Este formulário não está disponível.'}</p>
          </div>
        )}

        {state === 'done' && (
          <div className="bg-white rounded-3xl border border-brand-border p-8 text-center mt-16" data-testid="form-thank-you">
            <CheckCircle size={40} weight="fill" className="text-brand-green mx-auto mb-3" />
            <p className="text-brand-text font-medium">{message}</p>
          </div>
        )}

        {state === 'ready' && form && (
          <>
            {form.cover_image_path && (
              <img src={imageUrl(form.cover_image_path)} alt="" className="w-full h-40 object-cover rounded-3xl mb-5" />
            )}
            <div className="bg-white rounded-3xl border border-brand-border p-6" data-testid="form-card">
              <h1 className="font-heading text-2xl text-brand-green font-semibold mb-1.5">{form.title}</h1>
              {form.description && <p className="text-sm text-brand-muted mb-5 whitespace-pre-wrap">{form.description}</p>}
              <FormRenderer form={form} imageUrl={imageUrl} onUploadFile={uploadFile} onSubmit={handleSubmit} submitting={submitting} />
            </div>
          </>
        )}

        <p className="text-center text-xs text-brand-placeholder mt-6" data-testid="form-footer-signature">
          Ana Magalhães Pereira, nutricionista na área materno-infantil ({ERS_LABEL})
        </p>
      </div>
    </div>
  );
}
